import * as React from 'react'

import { BoxIcon } from 'lucide-react'
import { makeObservable, observable, runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { IFormMaterial, IPermitTypeField } from '~/client/graph'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import IPermitFieldsStore from '~/client/src/shared/models/IPermitFieldsStore'
import MaterialsStore from '~/client/src/shared/stores/domain/Materials.store'
import { EMPTY_STRING, NO_VALUE } from '~/client/src/shared/utils/usefulStrings'
import { copyObject } from '~/client/src/shared/utils/util'

import StruxhubTextValueSelector from '../../../StruxhubInputs/StruxhubSelector/StruxhubTextValueSelector'
import SitePermitCreationFormStore, {
  FormDropdownType,
} from '../../WorkflowCreationForm.store'
import PermitBaseFormField from './PermitBaseFormField'

interface IProps {
  typeField: IPermitTypeField
  isViewMode: boolean

  store: SitePermitCreationFormStore
  fieldsStore: IPermitFieldsStore

  tableId?: string
  tableRowIndex?: number
  parentInstanceId?: string
  parentValueIndex?: number

  materialsStore?: MaterialsStore
}

type FormMaterialKey = keyof IFormMaterial
const FormMaterialKey: { [K in FormMaterialKey]: K } = {
  materialId: 'materialId',
  procurementId: 'procurementId',
}

@inject('materialsStore')
@observer
export default class PermitFormMaterialField extends React.Component<IProps> {
  @observable private localProps: IProps = null

  public constructor(props: IProps) {
    super(props)
    this.localProps = props
    makeObservable(this)
  }

  public componentDidUpdate(prevProps: IProps) {
    if (prevProps !== this.props) {
      runInAction(() => {
        this.localProps = this.props
      })
    }
  }

  public render() {
    const {
      isViewMode,
      typeField,
      store,
      fieldsStore,
      parentInstanceId,
      parentValueIndex,
    } = this.props

    return (
      <PermitBaseFormField
        typeField={typeField}
        fieldValues={this.fieldValues}
        isViewMode={isViewMode}
        store={store}
        fieldsStore={fieldsStore}
        parentInstanceId={parentInstanceId}
        parentValueIndex={parentValueIndex}
        isForcedTitle
        componentRenderer={this.renderComponent}
        viewModeRenderer={this.renderViewMode}
        onAddFieldClick={this.addNewFieldValue}
        onRemoveField={this.removeValue}
      />
    )
  }

  private renderComponent = (
    value: IFormMaterial,
    index: number,
  ): JSX.Element => {
    const { typeField, materialsStore } = this.localProps
    const { caption, isMandatory } = typeField
    const { hasProcurementIds, getInstanceById: getMaterialById } =
      materialsStore
    const material = getMaterialById(value?.materialId)
    const procurementValue =
      material?.includesProcurementId(value?.procurementId) &&
      value?.procurementId

    return (
      <>
        <StruxhubTextValueSelector
          label={caption}
          isRequired={isMandatory}
          isChanged={this.isFieldChanged(FormMaterialKey.materialId, index)}
          value={material?.productName || EMPTY_STRING}
          onClick={this.onDropdownFieldClick.bind(
            this,
            index,
            FormDropdownType.MATERIAL,
            this.changeMaterialId,
          )}
        />
        {hasProcurementIds && (
          <>
            <StruxhubTextValueSelector
              label={Localization.translator.procurementID}
              isRequired={material?.hasProcurementIds}
              isChanged={this.isFieldChanged(
                FormMaterialKey.procurementId,
                index,
              )}
              value={procurementValue || EMPTY_STRING}
              onClick={this.onDropdownFieldClick.bind(
                this,
                index,
                FormDropdownType.PROCUREMENT_ID,
                this.changeProcurementId,
              )}
            />
          </>
        )}
      </>
    )
  }

  private renderViewMode = (value: IFormMaterial): JSX.Element => {
    const material = this.localProps.materialsStore.getInstanceById(
      value?.materialId,
    )

    if (!material?.productName) {
      return <div className="text large word-break-all">{NO_VALUE}</div>
    }

    return (
      <div className="tw:flex tw:gap-2 tw:items-center">
        <div className="tw:relative tw:shrink-0 tw:size-8 tw:rounded-full tw:bg-muted tw:flex tw:items-center tw:justify-center">
          <BoxIcon className="tw:block tw:size-5 tw:text-foreground" />
        </div>
        <div className="tw:flex tw:flex-col tw:items-start tw:min-w-0">
          <span className="tw:text-title-sm tw:text-foreground tw:break-words">
            {material.productName}
          </span>
        </div>
      </div>
    )
  }

  private get fieldValues(): IFormMaterial[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    const values = fieldsStore.getFieldValues<IFormMaterial>(
      typeField.id,
      tableId,
      tableRowIndex,
      false,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : [{ materialId: null }]
  }

  private get existingValues(): IFormMaterial[] {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps

    const values = fieldsStore.getFieldValues<IFormMaterial>(
      typeField.id,
      tableId,
      tableRowIndex,
      true,
      parentInstanceId,
      parentValueIndex,
    )

    return values?.length ? values : [{ materialId: null }]
  }

  private isFieldChanged = (key: FormMaterialKey, index: number): boolean => {
    const {
      fieldsStore,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.localProps
    const { editableEntity, isFieldChanged } = fieldsStore

    if (!editableEntity?.id) {
      return false
    }

    return (
      isFieldChanged(
        typeField.id,
        index,
        tableId,
        tableRowIndex,
        parentInstanceId,
        parentValueIndex,
      ) && this.fieldValues[index]?.[key] !== this.existingValues[index]?.[key]
    )
  }

  private onDropdownFieldClick(
    fieldIndex: number,
    dropdownType: FormDropdownType,
    onChange: (fieldIndex: number, newValue: string) => void,
  ) {
    const {
      store,
      typeField,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const {
      setSelectedField,
      setSelectedFieldDropdownType,
      setSelectedOnChangeHandler,
    } = store

    setSelectedField(
      typeField,
      fieldIndex,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )
    setSelectedFieldDropdownType(dropdownType)
    setSelectedOnChangeHandler(onChange.bind(this, fieldIndex))
  }

  private changeMaterialId(fieldIndex: number, newValue: string) {
    if (!newValue) {
      this.setMaterialValue(null, fieldIndex)
      return
    }

    const formMaterial = copyObject<IFormMaterial>(this.fieldValues[fieldIndex])
    const material = this.props.materialsStore.getInstanceById(newValue)
    const singleProcurement =
      material?.procurementDataList?.length === 1 &&
      material.procurementDataList[0]

    formMaterial.materialId = newValue
    formMaterial.procurementId = singleProcurement
      ? singleProcurement.procurementId
      : null

    this.setMaterialValue(formMaterial, fieldIndex)
  }

  private changeProcurementId(fieldIndex: number, newValue: string) {
    if (!newValue) {
      this.setMaterialValue(null, fieldIndex)
      return
    }

    const formMaterial = copyObject<IFormMaterial>(this.fieldValues[fieldIndex])

    formMaterial.procurementId = newValue

    const material = this.props.materialsStore.list.find(m =>
      m.includesProcurementId(newValue),
    )
    formMaterial.materialId = material?.id

    this.setMaterialValue(formMaterial, fieldIndex)
  }

  private changeValue(
    key: FormMaterialKey,
    fieldIndex: number,
    newValue: string,
  ) {
    const formMaterial = copyObject<IFormMaterial>(this.fieldValues[fieldIndex])

    formMaterial[key] = newValue

    this.setMaterialValue(formMaterial, fieldIndex)
  }

  private resetValue(key: FormMaterialKey, fieldIndex: number) {
    const material = copyObject<IFormMaterial>(this.fieldValues[fieldIndex])

    material[key] = null

    this.setMaterialValue(material, fieldIndex)
  }

  private setMaterialValue = (material: IFormMaterial, fieldIndex: number) => {
    const {
      typeField,
      fieldsStore,
      tableId,
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    } = this.props
    const { changeFieldValue, changeTableFieldValue, changeValidationState } =
      fieldsStore

    changeValidationState(
      typeField.id,
      fieldIndex,
      this.isMaterialValid(material),
      tableRowIndex,
      parentInstanceId,
      parentValueIndex,
    )

    if (this.isTableField) {
      changeTableFieldValue(
        tableId,
        tableRowIndex,
        typeField,
        fieldIndex,
        material,
        parentInstanceId,
        parentValueIndex,
      )
      return
    }

    changeFieldValue(
      typeField,
      fieldIndex,
      material,
      parentInstanceId,
      parentValueIndex,
    )
  }

  private get isTableField(): boolean {
    return !!this.props.tableId
  }

  private isMaterialValid = (material: IFormMaterial): boolean => {
    const { typeField } = this.props

    return (
      !material ||
      ((!typeField.isMandatory || !!material.materialId) &&
        (!this.isMaterialHasProcurementIds(material?.materialId) ||
          !!material.procurementId))
    )
  }

  private isMaterialHasProcurementIds(materialId: string): boolean {
    return this.props.materialsStore.getInstanceById(materialId)
      ?.hasProcurementIds
  }

  private addNewFieldValue = () => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props

      fieldsStore.addNewFieldValue(
        typeField,
        { materialId: null },
        parentInstanceId,
        parentValueIndex,
      )
    }
  }

  private removeValue = (index: number) => {
    if (!this.isTableField) {
      const { fieldsStore, typeField, parentInstanceId, parentValueIndex } =
        this.props
      const { rearrangeInvalidState, removeFieldValue } = fieldsStore

      rearrangeInvalidState(typeField.id, index)
      removeFieldValue(typeField.id, index, parentInstanceId, parentValueIndex)
    }
  }
}
